import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Handshake } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Vitrine des sponsors — réutilisable.
 * variant="home"   -> sponsors mis en avant (featured), avec titres + CTA.
 * variant="branch" -> sponsors d'une filière (branche). Se masque si aucun sponsor.
 */
export default function SponsorsShowcase({ variant = 'home', branche = null }) {
  const [sponsors, setSponsors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    const params = variant === 'home'
      ? { featured: true, limit: 12 }
      : { branche, limit: 12 };
    axios.get(`${API}/api/sponsors`, { params })
      .then((res) => { if (active) setSponsors(res.data.sponsors || []); })
      .catch(() => { if (active) setSponsors([]); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [variant, branche]);

  // Bloc filière : on ne rend rien s'il n'y a aucun sponsor
  if (variant === 'branch' && (!loaded || sponsors.length === 0)) return null;

  const logoGrid = (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="sponsors-showcase-grid">
      {sponsors.map((sp) => (
        <Link
          key={sp.id}
          to={`/partenaires-sponsors/${sp.slug}`}
          data-testid={`showcase-sponsor-${sp.slug}`}
          className="group bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-all min-h-[120px]"
          aria-label={`Découvrir le partenaire ${sp.name}`}
        >
          {sp.logo_url ? (
            <img
              src={sp.logo_url.startsWith('http') ? sp.logo_url : `${API}${sp.logo_url}`}
              alt={sp.name}
              className="max-h-12 max-w-full object-contain"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Handshake className="w-6 h-6 text-emerald-400" />
            </div>
          )}
          <span className="text-xs text-slate-300 text-center font-medium line-clamp-2 group-hover:text-white">{sp.name}</span>
        </Link>
      ))}
    </div>
  );

  if (variant === 'branch') {
    return (
      <section className="py-10" data-testid="sponsors-branch-block">
        <div className="max-w-6xl mx-auto px-4">
          <h3 className="text-xl font-bold text-white mb-1">Partenaires de cette filière</h3>
          <p className="text-slate-400 text-sm mb-6">Cette activité est soutenue par :</p>
          {logoGrid}
          <div className="mt-6">
            <Link to="/devenir-sponsor" data-testid="branch-become-sponsor"
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold text-sm">
              Devenir sponsor de cette filière <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // variant home
  return (
    <section className="py-20 bg-slate-950" data-testid="sponsors-home-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-3">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20">
            <Handshake className="w-3.5 h-3.5" /> Nos partenaires &amp; sponsors
          </span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
          Ils soutiennent l'écosystème <span className="text-emerald-400">coopératif ultramarin</span>
        </h2>
        <p className="text-slate-400 max-w-2xl mb-8 text-sm md:text-base">
          Entreprises, institutions et acteurs engagés contribuent au développement des filières,
          des projets et des territoires accompagnés par OBJECTIF SCOP OUTREMER.
        </p>

        {loaded && sponsors.length > 0 ? (
          <>{logoGrid}</>
        ) : (
          <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center" data-testid="sponsors-home-empty">
            <Handshake className="w-10 h-10 text-emerald-400/60 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Notre réseau de partenaires se construit. Rejoignez les premiers sponsors du réseau.</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-8">
          <Link to="/partenaires-sponsors" data-testid="home-discover-network"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all">
            Découvrir le réseau <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/devenir-sponsor" data-testid="home-become-sponsor"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-all">
            Devenir sponsor
          </Link>
          <Link to="/projets-a-soutenir" data-testid="home-support-project"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-all">
            Soutenir un projet
          </Link>
        </div>
      </div>
    </section>
  );
}
